import {Validation} from 'libs/validation/validation';
import {DoctorDetailsItemType} from 'types/successPage';

type ValidationFormatType = Record<
    string,
    {
        value: string;
    }
>;

const commonValidateRule = (value: string): string => {
    if (!value) return 'Please fill in all fields';
    return '';
};

/**
 * Validates an optional field and checks its a number
 */
export const optionalNumberRule = (value: string): string => {
    if (Number.isNaN(Number(value))) return 'Invalid field';
    return '';
};

const NAME_VALIDATION_REGEX = /^[A-Za-z]+([ ]+[A-Za-z]+)+$/;

/**
 * Validate patient name to make sure its at least two words and has no special characters
 */
export const validatePatientName = (value: string) => {
    const isValidLength = Boolean(value.trim().length);
    if (!isValidLength) return 'Please fill in all fields';

    return isValidLength && value.trim().match(NAME_VALIDATION_REGEX) ? '' : 'Invalid field';
};

const noValidateRule = () => '';

/**
 * Rules for validation fields
 * @returns {string | boolean} if there are no errors return false
 */
const rules = {
    city: commonValidateRule,
    email: noValidateRule,
    fax: optionalNumberRule,
    faxNumber: optionalNumberRule,
    firstName: noValidateRule,
    id: noValidateRule,
    lastName: noValidateRule,
    mailingAddress1stLine: noValidateRule,
    mailingAddressCity: noValidateRule,
    mailingAddressFax: noValidateRule,
    mailingAddressPhone: noValidateRule,
    mailingAddressState: noValidateRule,
    mailingAddressZipCode: noValidateRule,
    name: commonValidateRule,
    organizationName: noValidateRule,
    patientName: validatePatientName,
    phone: optionalNumberRule,
    regionCode: commonValidateRule,
};

/**
 * Rules for validation autocompleted fields
 * @returns {string | boolean} if there are no errors return false
 */
const autocompletedRules = {
    city: commonValidateRule,
    email: noValidateRule,
    fax: optionalNumberRule,
    firstName: noValidateRule,
    id: noValidateRule,
    lastName: noValidateRule,
    mailingAddress1stLine: noValidateRule,
    mailingAddressCity: noValidateRule,
    mailingAddressFax: noValidateRule,
    mailingAddressPhone: noValidateRule,
    mailingAddressState: noValidateRule,
    mailingAddressZipCode: noValidateRule,
    name: commonValidateRule,
    organizationName: noValidateRule,
    patientName: validatePatientName,
    phone: noValidateRule,
    regionCode: commonValidateRule,
};

/**
 * Helper to convert data to valid format for our validator (field value is required)
 * @param {Partial<FieldType>}  values - from form dr details
 * @return {ValidationFormatType} result which one is valid to use in new Validation
 */
const convertDrDetailsValues = (
    values: Partial<DoctorDetailsItemType['doctorDetails']>,
): ValidationFormatType => {
    const details: ValidationFormatType = {};
    Object.entries(values).forEach(([key, value]) => {
        if (key !== 'filling_method' && key !== 'location') {
            details[key] = {value} as {value: string};
        }
    });
    return details;
};

type FieldType<Type> = keyof Type;
type ResultType<Type> = {[key in FieldType<Type>]?: string | boolean};

/**
 * Provides validation for dr details
 * @param {Pick<DoctorDetailsItemType, 'doctorDetails'>}  item - from form dr details
 * @return {boolean} - result of validation
 */
export const validateDrDetails = (
    item: DoctorDetailsItemType,
): ResultType<ValidationFormatType> => {
    if (item.isAutocompleteModeEnabled) {
        const validationResult = new Validation(
            convertDrDetailsValues(item.doctorDetails),
            autocompletedRules,
        ).all();
        if (!item.doctorDetails.firstName && !item.doctorDetails.organizationName) {
            return typeof validationResult === 'boolean'
                ? {firstName: 'Please select doctor or clinic from the list'}
                : {...validationResult, firstName: 'Please select doctor or clinic from the list'};
        }
        return typeof validationResult === 'boolean' ? {} : {...validationResult};
    }

    const manualValidationResult = new Validation(
        convertDrDetailsValues(item.doctorDetails),
        rules,
    ).all();

    return typeof manualValidationResult === 'boolean' ? {} : {...manualValidationResult};
};
